import { ArrowLeft, MailIcon, Phone } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import { Card, CardHeader } from '@/components/ui/Card';
import { Avatar } from '@/components/ui/Avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { useCustomerDetail, useRequests, usePayments } from '@/data/api';
import { ErrorState } from '@/components/common/ErrorState';
import { formatKes, formatRelative, maskAccount, maskEmail, maskPhone } from '@/lib/format';
import { REQUEST_TYPE_LABEL } from '@/types/request';
import { StatusPill } from '@/components/ui/StatusPill';

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const detailQuery = useCustomerDetail(id ?? null);
  const [tab, setTab] = useState('overview');

  // Cross-load the customer's recent requests + payments by account number.
  const accountNumber = detailQuery.data?.accountNumber ?? '';
  const requestsQuery = useRequests({
    search: accountNumber || undefined,
    page: 0,
    size: 12,
  });
  const paymentsQuery = usePayments({
    search: accountNumber || undefined,
    page: 0,
    size: 12,
  });
  const requests = requestsQuery.data?.content ?? [];
  const payments = paymentsQuery.data?.content ?? [];

  if (detailQuery.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-48" />
      </div>
    );
  }
  if (detailQuery.isError) {
    return (
      <ErrorState
        title="Couldn't load customer"
        description={(detailQuery.error as Error)?.message}
        onRetry={() => detailQuery.refetch()}
      />
    );
  }
  if (!detailQuery.data) {
    return (
      <Card>
        <h2 className="text-h2">Customer not found</h2>
        <Link to="/customers" className="text-brand-blue text-caption font-semibold">
          ← Back to customers
        </Link>
      </Card>
    );
  }

  // Map the legacy customer_accounts row to the props the existing UI expects.
  const cd = detailQuery.data;
  const customer = {
    id: String(cd.accountId),
    name: cd.accountName ?? '—',
    phone: cd.mobileNumber ?? '—',
    email: cd.email,
    verifiedAccountsCount: 1,
    totalPaidKes: 0,
    lastActiveAt: new Date().toISOString(),
    status: (cd.accountStatus ?? 'ACTIVE').toUpperCase().includes('ACTIVE')
      ? 'ACTIVE'
      : 'SUSPENDED',
    accounts: cd.accountNumber
      ? [{
          id: String(cd.accountId),
          accountNumber: cd.accountNumber,
          balance: cd.balance ?? 0,
          location: cd.pipelineCode ?? '',
        }]
      : [],
  } as const;

  return (
    <div className="flex flex-col gap-4">
      <Link
        to="/customers"
        className="inline-flex items-center gap-1.5 text-caption font-semibold text-brand-blue hover:underline w-fit"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to customers
      </Link>

      <Card>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <Avatar name={customer.name} size="lg" />
            <div>
              <h2 className="text-h1 text-ink">{customer.name}</h2>
              <div className="flex items-center gap-3 mt-1 text-caption text-ink-muted">
                <span className="inline-flex items-center gap-1">
                  <Phone className="h-3.5 w-3.5" />
                  <span className="font-mono">{maskPhone(customer.phone)}</span>
                </span>
                <span className="inline-flex items-center gap-1">
                  <MailIcon className="h-3.5 w-3.5" />
                  {maskEmail(customer.email)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm">Message</Button>
            <Button variant="ghost" size="sm">{customer.status === 'ACTIVE' ? 'Suspend' : 'Reinstate'}</Button>
          </div>
        </div>
      </Card>

      <Card padding="none">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="px-2">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="accounts">Linked accounts</TabsTrigger>
            <TabsTrigger value="requests">Service requests</TabsTrigger>
            <TabsTrigger value="payments">Payments</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard label="Verified accounts" value={customer.verifiedAccountsCount} />
              <StatCard label="Total paid" value={`KES ${formatKes(customer.totalPaidKes)}`} />
              <StatCard label="Last active" value={formatRelative(customer.lastActiveAt)} />
            </div>
          </TabsContent>

          <TabsContent value="accounts">
            <div className="p-5 space-y-2">
              {(customer.accounts ?? []).map((a) => (
                <div
                  key={a.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-line p-3"
                >
                  <div>
                    <div className="font-mono text-body-strong text-ink">{a.accountNumber}</div>
                    <div className="text-caption text-ink-muted">{a.location}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-caption text-ink-muted">Balance</div>
                    <div className="font-mono tabular text-ink">KES {formatKes(a.balance)}</div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="requests">
            <div className="p-5">
              <CardHeader title="Recent service requests" description="Up to 12 most recent." />
              <table className="w-full text-body">
                <thead>
                  <tr className="text-overline text-ink-hint uppercase">
                    <th className="text-left py-2">Ref</th>
                    <th className="text-left py-2">Type</th>
                    <th className="text-left py-2">Submitted</th>
                    <th className="text-left py-2">Status</th>
                    <th className="text-right py-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => (
                    <tr key={r.id} className="border-t border-line-soft">
                      <td className="py-2.5 font-mono text-caption">{r.referenceNumber}</td>
                      <td className="py-2.5">{REQUEST_TYPE_LABEL[r.requestType]}</td>
                      <td className="py-2.5 text-ink-muted">{formatRelative(r.submittedAt)}</td>
                      <td className="py-2.5"><StatusPill status={r.status} /></td>
                      <td className="py-2.5 text-right font-mono tabular">
                        {r.totalAmount ? `KES ${formatKes(r.totalAmount)}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="payments">
            <div className="p-5">
              <CardHeader title="Recent payments" />
              <table className="w-full text-body">
                <thead>
                  <tr className="text-overline text-ink-hint uppercase">
                    <th className="text-left py-2">Ref</th>
                    <th className="text-left py-2">Account</th>
                    <th className="text-left py-2">Method</th>
                    <th className="text-right py-2">Amount</th>
                    <th className="text-left py-2 pl-6">Status</th>
                    <th className="text-left py-2 pl-6">Initiated</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-t border-line-soft">
                      <td className="py-2.5 font-mono text-caption">{p.referenceNumber}</td>
                      <td className="py-2.5 font-mono">{maskAccount(p.accountNumber)}</td>
                      <td className="py-2.5">{p.method.replace('_', ' ')}</td>
                      <td className="py-2.5 text-right font-mono tabular">KES {formatKes(p.amount)}</td>
                      <td className="py-2.5 pl-6">
                        <span className="text-caption font-semibold">
                          {p.status}
                        </span>
                      </td>
                      <td className="py-2.5 pl-6 text-ink-muted">{formatRelative(p.initiatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-line p-4">
      <div className="text-caption text-ink-muted">{label}</div>
      <div className="text-h2 text-ink mt-1 tabular">{value}</div>
    </div>
  );
}
